import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import DownloadList from '../components/Profile/DownloadList';
import { fetchDownloads, deleteAccount } from '../services/api';

interface ProfileProps {
  onLogout: () => void;
}

export default function Profile({ onLogout }: ProfileProps) {
  const { user, refreshUser, setUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [downloads, setDownloads] = useState<any[]>([]);
  const [downloadsLoading, setDownloadsLoading] = useState(true);

  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        await refreshUser(token);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch user');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, refreshUser]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setDownloadsLoading(true);
      try {
        const data = await fetchDownloads(token, user?.email);
        setDownloads(data);
      } catch (err) {
        console.error(err);
      } finally {
        setDownloadsLoading(false);
      }
    })();
  }, [token, user?.email]);

  const handleLogout = () => {
    onLogout();
    setUser(null);
  };

  const handleDeleteAccount = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete your account? This action cannot be undone.'
      )
    )
      return;

    setDeleteLoading(true);
    try {
      await deleteAccount(token!);
      handleLogout();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-bold">Access Denied</h2>
        <p>You must be logged in to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-12 p-6 bg-white dark:bg-neutral-800 rounded shadow">
      <h2 className="text-2xl font-bold mb-4">Profile</h2>
      <p className="mb-2">
        <strong>Email:</strong> {user?.email}
      </p>

      <div className="mt-6">
        <h3 className="text-xl font-semibold mb-2">
          Your Downloads
          {!downloadsLoading && ` (${downloads.length})`}
        </h3>
        {downloadsLoading ? (
          <p>Loading downloads...</p>
        ) : downloads.length === 0 ? (
          <p>No downloads found.</p>
        ) : (
          <div className="mt-6">
            {/* On passe userEmail directement — DownloadCard gère le modal lui-même */}
            <DownloadList
              downloads={downloads}
              loading={downloadsLoading}
              userEmail={user?.email}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 mt-4">
        <button
          onClick={handleDeleteAccount}
          disabled={deleteLoading}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
        >
          {deleteLoading ? 'Deleting...' : 'Delete Account'}
        </button>
      </div>
    </div>
  );
}
